import {DndContext, DragOverlay} from '@dnd-kit/core';
import {AnimatePresence, motion} from 'framer-motion';
import {match} from 'ts-pattern';

import {
  startTransition,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {getSafely} from 'shared/web';

import {useHttpCalls} from './api/useHttpCalls';
import {BulkActionsBar} from './components/BulkActionsBar/BulkActionsBar';
import {DefaultEmptyStateRenderer} from './components/DefaultEmptyStateRenderer/DefaultEmptyStateRenderer';
import {DragOverlayColumn} from './components/DragOverlayColumn/DragOverlayColumn';
import {ExternalFiltersPortal} from './components/ExternalFiltersPortal/ExternalFiltersPortal';
import {GridBody} from './components/GridBody/GridBody';
import {GridHeader} from './components/GridHeader/GridHeader';
import {HeaderRow} from './components/HeaderRow/HeaderRow';
import {GridPagination} from './components/Pagination/Pagination';
import {Sidebar} from './components/Sidebar/Sidebar';
import {DEFAULT_COL_WIDTH} from './constants/defaultColWidth';
import {SELECTION_COLUMN_WIDTH} from './constants/selectionColumn';
import {ColumnMenuContext} from './context/ColumnMenuContext';
import {useDataGridContext} from './context/useDataGridContext';
import {useBulkActionExecution} from './hooks/useBulkActionExecution';
import {useBulkActions} from './hooks/useBulkActions';
import {useColumnAutosize} from './hooks/useColumnAutosize';
import {useColumnDrag} from './hooks/useColumnDrag';
import {useColumnResize} from './hooks/useColumnResize';
import {useGridActionColumn} from './hooks/useGridActionColumn';
import {useGridDataFetching} from './hooks/useGridDataFetching';
import {useGridDragAndDrop} from './hooks/useGridDragAndDrop';
import {useGridFilterSync} from './hooks/useGridFilterSync';
import {useGridSelection} from './hooks/useGridSelection';
import {useHandleLink} from './hooks/useHandleLink';
import {useIsMobileView} from './hooks/useIsMobileView';
import {usePageUrlSync} from './hooks/usePageUrlSync';
import {usePresetSync} from './hooks/usePresetSync';
import {useRowActionClick} from './hooks/useRowActionClick';
import {useServerData} from './hooks/useServerData';
import {
  selectActiveDragId,
  selectActivePreset,
  selectColumnGroupsVisible,
  selectDataQuery,
  selectIsBulkActionLoading,
  selectQueryId,
} from './store/createEagGridStore';
import {
  useColumnMovingAllowed,
  useActionColumnEnabled,
  usePaginationEnabled,
  useColumnMenuEnabled,
  useColumnPinningAllowed,
  useColumnAutoSizingAllowed,
} from './store/selectors';
import {useActionsDefinition} from './store/selectors/useActionsDefinition';
import {useActivePanel} from './store/selectors/useActivePanel';
import {selectRowSelectMode} from './store/selectors/useBehavior';
import {useColumnStates} from './store/selectors/useColumnStates';
import {useFilterValues} from './store/selectors/useFilterValues';
import {useGridIsLoading} from './store/selectors/useGridIsLoading';
import {useGridPage} from './store/selectors/useGridPage';
import {useGridPreviousRows} from './store/selectors/useGridPreviousRows';
import {useGridRows} from './store/selectors/useGridRows';
import {useGridSettingsSafe} from './store/selectors/useGridSettingsSafe';
import {useGridTotalCount} from './store/selectors/useGridTotalCount';
import {useIsColumnsReady} from './store/selectors/useIsColumnsReady';
import {useIsFilterActive} from './store/selectors/useIsFilterActive';
import {useOrderedVisibleColumns} from './store/selectors/useOrderedVisibleColumns';
import {usePinnedLeftColumns} from './store/selectors/usePinnedLeftColumns';
import {usePinnedRightColumns} from './store/selectors/usePinnedRightColumns';
import {useSelectedRowIds} from './store/selectors/useSelectedRowIds';
import {useVisibleColumns} from './store/selectors/useVisibleColumns';
import {useEagGridSelector} from './store/useEagGridSelector';
import {useEagGridStore} from './store/useEagGridStore';
import type {EagColumn} from './types/EagColumn';
import type {EagColumnState} from './types/EagColumnState';
import type {EagGridProps} from './types/EagGridProps';
import type {EagGridTableInnerProps} from './types/EagGridTableInnerProps';
import type {EagGridTableProps} from './types/EagGridTableProps';
import type {RowData} from './types/RowData';
import {ToolpanelType} from './types/ToolpanelType';
import {columnToInitialState} from './utils/columnToInitialState';
import {computeColIndexMap} from './utils/computeColIndexMap';
import {computeSortModelKey} from './utils/computeSortModelKey';
import {flattenColumnConfig} from './utils/flattenColumnConfig';
import {getColumnData} from './utils/getColumnData';
import {isFilterValueActive} from './utils/isFilterValueActive';
import {LineHeightToPx} from './utils/lineHeightToValue';
import {setSourceSearchParamsByGridCode} from './utils/url/setSourceSearchParamsByGridCode';

const BULK_BAR_ANIMATION = {
  initial: {height: 0, opacity: 0, overflow: 'hidden'},
  animate: {height: 'auto', opacity: 1, overflow: 'hidden'},
  exit: {height: 0, opacity: 0, overflow: 'hidden'},
  transition: {duration: 0.2, ease: 'easeInOut'},
};

/**
 * Core grid component that owns the table layout, header, sidebar, and data
 * fetching lifecycle for a single EagGrid instance.
 *
 * ## State management — why no prop drilling
 *
 * EagGridTable deliberately does **not** accept or forward low-level state slices
 * (loading, rows, column widths, drag state, etc.) as props.  Instead it reads
 * and mutates state through `useEagGridStore()` / `useEagGridSelector()`, which
 * reach the store provided by the parent `EagGridStoreProvider`.
 *
 * This keeps the component surface small and prevents prop explosion as new UI
 * features are added.  Specifically for the filter system redesign:
 *
 * - `filterValues` flows from the store into `handleFilterChange`
 *   and `handleResetFilters`, then gets passed to the sidebar as callbacks —
 *   no intermediate component re-render required.
 * - `activePanel` lives in the store; GridHeader toggles it, Sidebar
 *   reads it, and the new FilterPanel close button dispatches `setActivePanel(null)`
 *   — all without EagGridTable acting as a message bus.
 * - Drag state (`activeDragId`, `overColumnId`, `dropSide`) is written here and
 *   read directly by `HeaderCell` / `GridCell` 4 levels down — zero
 *   intermediary props.
 *
 * ## Column config stability
 *
 * `columnConfig` is treated as stable after mount.  Dynamic column changes require
 * remounting the component (pass a new `key` prop to the parent `<EagGrid>`).
 * This is enforced by the empty-dep `useLayoutEffect` that initialises column states.
 *
 * ## Filter panel integration
 *
 * `handleFilterChange` and `handleResetFilters` write directly to the store via
 * `store.setFilterValues`. The sidebar `FilterPanel` receives these callbacks
 * without any context subscription re-renders.
 */
function EagGridTableInner(props: EagGridTableInnerProps) {
  /**
   * Extract props to local bindings for readability.
   * Props are extracted for shorter names in JSX, not to break a taint chain —
   * EagGridTableInnerProps has no `ref` field, so there is no compiler taint.
   */
  const autoHeight = props.autoHeight;
  const gridCode = props.gridCode;
  const columnConfig = props.columnConfig;
  const definition = props.definition;
  const emptyStateNode = <DefaultEmptyStateRenderer emptyStatus={props.emptyState} />;
  const actionsDefinition = useActionsDefinition();
  const onRowSelectionChange = props.onRowSelectionChange;
  const testId = props['data-testid'];
  const queryModifier = props.queryModifier;
  const predefinedColumns = props.predefinedColumns;
  const actionCallback = props.actionCallback;

  const gridContext = useDataGridContext();
  const store = useEagGridStore();

  // Filter state from store — single source of truth (replaces DatagridFiltersModelProvider context).
  const filterValues = useFilterValues();
  const isFilterActive = useIsFilterActive();

  // Behavior flags from store
  const columnMovingAllowed = useColumnMovingAllowed();
  const actionColumnEnabled = useActionColumnEnabled();
  const paginationEnabled = usePaginationEnabled();
  const columnMenuEnabled = useColumnMenuEnabled();
  const columnPinningAllowed = useColumnPinningAllowed();
  const columnAutoSizingAllowed = useColumnAutoSizingAllowed();

  // columnConfig is treated as stable after mount.
  // Dynamic column changes require remounting this component (use a key prop).
  // useLayoutEffect runs synchronously before paint so the first visible render
  // already has column states — no flicker. It must NOT be done during render
  // because store.setState notifies useSyncExternalStore subscribers in other
  // components, which triggers the "update while rendering" React warning.
  useLayoutEffect(() => {
    const flatCols = flattenColumnConfig(columnConfig);
    store.setColumnStates(flatCols.map(columnToInitialState));
    // columnConfig is treated as stable after mount (dynamic changes require remounting via key prop).
    // store is ref-stable. Adding both as deps is safe — they never cause extra runs in practice.
  }, [columnConfig, store]);

  const dataQuery = useEagGridSelector(selectDataQuery)!;

  const selectedRowIds = useSelectedRowIds();
  // rowSelectMode is a string primitive — safe with Object.is() comparison, no referential stability concern.
  const rowSelectMode = useEagGridSelector(selectRowSelectMode);
  // hasCheckboxColumn is derived from rowSelectMode — no additional subscription needed.
  const hasCheckboxColumn = rowSelectMode !== 'NONE';
  const page = useGridPage();
  const columnStates = useColumnStates();
  const visibleColumnStates = useVisibleColumns();
  // Ordered for rendering: [pinnedLeft | unpinned | pinnedRight].
  // visibleColumnStates (store order) is kept for DnD operations that need the raw order.
  const orderedVisibleColumnStates = useOrderedVisibleColumns();
  const pinnedLeftColumns = usePinnedLeftColumns();
  const pinnedRightColumns = usePinnedRightColumns();
  // False until the first applyPresetColumnsAndSort call resolves column widths from the preset.
  // Suppresses the header+column skeleton to prevent width-jump flash on initial load.
  const isColumnsReady = useIsColumnsReady();

  const nonRightPinnedColumnStates = orderedVisibleColumnStates.filter(
    (cs) => cs.pinned !== 'RIGHT'
  );
  const rightPinnedColumnStates = orderedVisibleColumnStates.filter((cs) => cs.pinned === 'RIGHT');

  /**
   * @mandatory Maps column keys to their <col> DOM index in the colgroup for resize.
   * Right-pinned columns shift by +1 due to the filler <col> between unpinned and right-pinned.
   * hasCheckboxColumn adds +1 to all indices for the checkbox <col> at position 0.
   * Must be referentially stable: used as a prop passed to HeaderRow.colIndexMap.
   */
  const colIndexMap = useMemo(
    () =>
      computeColIndexMap(
        orderedVisibleColumnStates,
        nonRightPinnedColumnStates.length,
        hasCheckboxColumn
      ),
    [orderedVisibleColumnStates, nonRightPinnedColumnStates.length, hasCheckboxColumn]
  );

  // columnConfig is stable after mount (dynamic changes require remounting via key prop),
  // so this memo runs only once in practice.
  const flatColumns = useMemo(() => flattenColumnConfig(columnConfig), [columnConfig]);

  /**
   * @mandatory O(1) width lookup Map used by getWidth() inside useColumnResize.
   * new Map() from array data is not guaranteed referentially stable by React Compiler.
   */
  const columnWidthMap = useMemo(
    () => new Map(columnStates.map((cs) => [cs.key, cs.width ?? DEFAULT_COL_WIDTH])),
    [columnStates]
  );

  const getWidth = (key: string): number => columnWidthMap.get(key) ?? DEFAULT_COL_WIDTH;

  const colgroupRef = useRef<HTMLTableColElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const resizeManager = useColumnResize(colgroupRef, getWidth);
  const dragManager = useColumnDrag(visibleColumnStates, store.reorderColumnsByKey);
  const handleLink = useHandleLink();

  const http = useHttpCalls({gridCode});

  // --- Extracted hooks ---

  const dnd = useGridDragAndDrop({
    visibleColumnStates,
    store,
    dragManager,
  });

  /**
   * @mandatory Reference-stable Map of sticky left CSS offsets for pinned-left columns.
   * Passed as prop to HeaderRow and GridBody which render it into N*M cells.
   * Unstable reference would cause all rows to re-render on every unrelated state change.
   */
  const pinnedLeftOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let accum = hasCheckboxColumn ? SELECTION_COLUMN_WIDTH : 0;
    for (const cs of pinnedLeftColumns) {
      offsets.set(cs.key, accum);
      accum += cs.width ?? DEFAULT_COL_WIDTH;
    }
    return offsets;
  }, [pinnedLeftColumns, hasCheckboxColumn]);

  /**
   * @mandatory Reference-stable sort model: prevents spurious data refetches when column
   * reorder/resize changes columnStates order but does NOT change the actual sort configuration.
   * sortModelKey (derived below) uses this stable reference as input.
   */
  const sortModel = useMemo(
    () =>
      visibleColumnStates
        .filter((cs): cs is EagColumnState & {sort: 'ASC' | 'DESC'} => cs.sort != null)
        .map((cs) => ({columnKey: cs.key, order: cs.sort})),
    [visibleColumnStates]
  );

  // Stable string key derived from sort model — used as fetchPage dep so column reorder
  // or pin changes (which don't affect sort) do not trigger a spurious data refetch.
  // Sorting by columnKey ensures the key is order-independent.
  const sortModelKey = computeSortModelKey(sortModel);

  const gridSettings = useGridSettingsSafe();
  const itemsPerPage = gridSettings.itemsPerPage;
  const offset = page * itemsPerPage;
  const isMobile = useIsMobileView();

  const serverData = useServerData({
    gridCode,
    queryModifier,
    predefinedColumns,
  });

  const rows = useGridRows();
  const previousRows = useGridPreviousRows();
  const isLoading = useGridIsLoading();
  const isEmptyState = rows.length === 0 && !isLoading && isColumnsReady;

  const totalCount = useGridTotalCount();
  const queryId = useEagGridSelector(selectQueryId);

  const {actionColumnWidth} = useGridActionColumn({
    rows,
    previousRows,
    actionsDefinition,
    gridSettings,
    actionColumnEnabled,
    isMobile,
  });

  /**
   * @mandatory Reference-stable Map of sticky right CSS offsets for pinned-right columns.
   * Same rationale as pinnedLeftOffsets. Starts from actionColumnWidth so right-pinned
   * columns sit to the left of the fixed action column (sticky at right: 0).
   */
  const pinnedRightOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let accum = actionColumnWidth;
    for (const cs of [...pinnedRightColumns].reverse()) {
      offsets.set(cs.key, accum);
      accum += cs.width ?? DEFAULT_COL_WIDTH;
    }
    return offsets;
  }, [pinnedRightColumns, actionColumnWidth]);

  const {refreshData, serverDataRef, offsetRef, queryIdRef} = useGridDataFetching({
    gridCode,
    store,
    serverData,
    offset,
    filterValues,
    sortModelKey,
    gridSettings,
    queryId,
    refreshRef: props.refreshRef,
  });

  const activeDragId = useEagGridSelector(selectActiveDragId);
  const columnGroupsVisible = useEagGridSelector(selectColumnGroupsVisible);
  const activePresetFromStore = useEagGridSelector(selectActivePreset);
  const activePanel = useActivePanel();

  const handleTogglePanel = (panel: ToolpanelType) => {
    const current = store.getState().activePanel;
    store.setActivePanel(current === panel ? null : panel);
  };

  const handleClosePanel = () => {
    store.setActivePanel(null);
  };

  const handleTableAreaClick = () => {
    if (store.getState().activePanel != null) {
      store.setActivePanel(null);
    }
  };

  const filterValuesRef = useRef(filterValues);

  // deselectAll() is called here (not in an effect) — Vercel: rerender-move-effect-to-event.
  // deselectAll has a no-op guard so it is safe to call even when nothing is selected.
  // filterValuesRef always points to latest filterValues so no stale closure risk.
  // When value is inactive (null/false/empty), the key is removed rather than stored as
  // {value: null} to prevent sanitizeObject leaving behind {key: {}} tombstones that
  // incorrectly keep useIsFilterActive returning true.
  const handleFilterChange = (key: string, value: unknown) => {
    store.deselectAll();
    const next = {...filterValuesRef.current};
    if (isFilterValueActive(value)) {
      next[key] = value as Record<string, unknown>;
    } else {
      delete next[key];
    }
    store.setFilterValues(next);
  };

  const handleResetFilters = () => {
    store.deselectAll();
    store.resetFilterValues();
  };

  /**
   * @mandatory O(1) lookup Map of EagColumn by key, passed to useColumnAutosize.
   * new Map() from array is not guaranteed referentially stable by React Compiler.
   */
  const flatColumnMap = useMemo(
    () => new Map(flatColumns.map((col) => [col.key, col])),
    [flatColumns]
  );

  const {autosizeColumn: handleAutosizeColumn, autosizeAllColumns: handleAutosizeAllColumns} =
    useColumnAutosize(tableRef, flatColumnMap, columnMenuEnabled);

  const handleResetColumns = () => {
    // Show loading indicator immediately — the DELETE + GET round-trip takes time
    // and applyPresetColumnsAndSort only updates column state (does not always trigger a
    // data refetch), so without this the bar might never appear.
    store.setIsLoadingWithSnapshot();
    http.deleteVirtualPreset().then((freshPreset) => {
      if (freshPreset) {
        store.applyPresetColumnsAndSort(freshPreset.columnsSettings);
      } else {
        // DELETE succeeded but no fresh preset returned — stop loading.
        store.setIsLoading(false);
      }
    });
  };

  const handlePinColumn = (key: string, pinned: 'LEFT' | 'RIGHT' | null) => {
    store.setColumnPinned(key, pinned);
  };

  const handleSortDirect = (key: string, sort: 'ASC' | 'DESC' | null) => {
    // Non-urgent: sort change triggers data refetch + full grid re-render.
    startTransition(() => {
      store.setColumnSort(key, sort);
    });
  };

  usePresetSync({
    columnStates,
    filters: filterValues,
    onFiltersChange: store.setFilterValues,
    activePreset: activePresetFromStore!,
    settings: gridSettings,
  });

  const {handleToggleSelection, handleDeselectAll} = useGridSelection({
    store,
    rowSelectMode,
    selectedRowIds,
    rows,
    onRowSelectionChange,
  });

  useGridFilterSync({
    filterValues,
    onExternalFilterChange: gridContext.onExternalFilterChange,
  });

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs filterValuesRef every render for handleFilterChange stable closure
  useEffect(() => {
    filterValuesRef.current = filterValues;
  });

  /**
   * @mandatory O(1) column state Map passed into columnMenuContextValue.
   * Must be referentially stable — it is a dep of the columnMenuContextValue useMemo,
   * so an unstable Map would cause the context value to rebuild on every render.
   */
  const columnStateMap = useMemo(
    () => new Map(columnStates.map((cs) => [cs.key, cs])),
    [columnStates]
  );

  const handleSort = (key: string) => {
    // Early return if sorting is disabled via columnSortMode behavior flag
    const sortMode = store.getState().definition?.behavior?.columnSortMode;
    if (sortMode === 'NONE') {
      return;
    }
    const currentState = store.getState().columnStates.find((cs) => cs.key === key);
    if (!currentState) {
      return;
    }
    const nextSort = match(currentState.sort)
      .with(null, () => 'ASC' as const)
      .with(undefined, () => 'ASC' as const)
      .with('ASC', () => 'DESC' as const)
      .with('DESC', () => null)
      .exhaustive();
    // Non-urgent: sort change triggers data refetch + full grid re-render.
    startTransition(() => {
      store.setColumnSort(key, nextSort);
    });
  };

  const handleRowClick = (row: RowData, event: React.MouseEvent) => {
    // row.id is typed string on RowData — no cast needed
    match(rowSelectMode)
      .with('MULTIPLE', () => store.toggleRowSelection(row.id))
      .with('SINGLE', () => store.setSingleSelection(row.id))
      .with('NONE', () => {
        /* no selection */
      })
      .exhaustive();
    // onRowSelectionChange is fired via the selectedRowIds effect in useGridSelection (covers all selection paths).

    const rowClickActionKey = row.actions.rowClick ?? null;

    if (
      rowClickActionKey &&
      actionCallback &&
      actionsDefinition &&
      getSafely(actionsDefinition, rowClickActionKey)
    ) {
      const result = actionCallback({
        actionKey: rowClickActionKey,
        rowId: row.id,
        getColumnData: getColumnData(row, gridCode),
        rowData: row,
        refreshData: () => serverDataRef.current.fetchPage(offsetRef.current),
        refreshCells: () => undefined,
        redrawRows: () => undefined,
        deselectAll: () => store.deselectAll(),
        sourceSearchParams: setSourceSearchParamsByGridCode(gridCode, String(row.rowNumber)),
        queryId: queryIdRef.current ?? '',
        // Row click does not participate in action locking — no-op control
        actionLock: {lock: () => undefined, unlock: () => undefined},
      });

      if (typeof result === 'string') {
        handleLink(result, event.nativeEvent as MouseEvent);
      }
    }
  };

  // handleActionClick: fires when an action button in RowActions is clicked.
  // useRowActionClick manages per-action locking (Promise-based + manual dialog pattern).
  const handleActionClick = useRowActionClick(
    actionCallback,
    gridCode,
    queryIdRef as React.RefObject<string>,
    () => serverDataRef.current.fetchPage(offsetRef.current)
  );

  // visibleColumns is derived from orderedVisibleColumnStates so that the rendered
  // column DOM order is always [pinnedLeft | unpinned | pinnedRight] — required for
  // CSS `position: sticky` with left/right offsets to work correctly on table cells.
  // DnD operations use visibleColumnStates (store order) separately.
  const visibleColumns = useMemo(() => {
    const map = new Map(flatColumns.map((col) => [col.key, col]));
    return orderedVisibleColumnStates
      .map((cs) => map.get(cs.key))
      .filter((col): col is EagColumn => col !== undefined);
  }, [flatColumns, orderedVisibleColumnStates]);

  const selectedRowsActions = useMemo(() => {
    if (selectedRowIds.size === 0) {
      return [];
    }
    return rows.filter((row) => selectedRowIds.has(row.id)).map((row) => row.actions);
  }, [rows, selectedRowIds]);

  const bulkActions = useBulkActions(actionsDefinition ?? {}, selectedRowsActions);

  // deselectAll() called here to clear stale row IDs when navigating to a different page.
  // Vercel: rerender-move-effect-to-event — no effect or ref needed.
  const handlePageChange = (p: number) => {
    store.deselectAll();
    store.setPage(p - 1);
  };

  usePageUrlSync({
    gridCode,
    page,
    filterValues: filterValues ?? {},
    queryId: queryId ?? '',
  });

  /**
   * @mandatory O(1) lookup Maps for DragOverlay column resolution.
   * new Map() from array is not guaranteed referentially stable by React Compiler.
   */
  const visibleColumnMap = useMemo(
    () => new Map(visibleColumns.map((col) => [col.key, col])),
    [visibleColumns]
  );
  const visibleColumnStateMap = useMemo(
    () => new Map(orderedVisibleColumnStates.map((cs) => [cs.key, cs])),
    [orderedVisibleColumnStates]
  );

  const dragOverlayColumn = activeDragId ? (visibleColumnMap.get(activeDragId) ?? null) : null;
  const dragOverlayColumnState = activeDragId
    ? (visibleColumnStateMap.get(activeDragId) ?? null)
    : null;

  // handleBulkAction defined after refreshData so the stable ref-backed refreshData callback
  // can be passed through to actionCallback params — consumers can call refreshData() to
  // reload the grid after a bulk operation (e.g. bulk delete).
  const handleBulkAction = useBulkActionExecution({
    actionCallback,
    rows,
    selectedRowIds,
    gridCode,
    refreshData,
  });

  const isBulkActionLoading = useEagGridSelector(selectIsBulkActionLoading);

  // Pinning edge keys: last left-pinned column key and first right-pinned column key.
  // Used to add drop shadows on the boundary between pinned and non-pinned cells.
  const lastPinnedLeftKey =
    pinnedLeftColumns.length > 0
      ? (pinnedLeftColumns[pinnedLeftColumns.length - 1]?.key ?? null)
      : null;
  const firstPinnedRightKey =
    pinnedRightColumns.length > 0 ? (pinnedRightColumns[0]?.key ?? null) : null;

  // Feature flags from behavior selectors (set by backend, not user settings).
  const showColumnMenu = columnMenuEnabled;
  const showPinning = columnPinningAllowed;
  const showAutosize = columnAutoSizingAllowed;

  /**
   * @mandatory Context.Provider value must be referentially stable.
   * Without useMemo, every render creates a new object reference,
   * causing all ColumnMenuContext consumers to re-render regardless of actual changes.
   * React Compiler does not guarantee reference stability for Provider value objects.
   */
  const columnMenuContextValue = useMemo(
    () => ({
      showColumnMenu,
      showPinning,
      showAutosize,
      filterState: filterValues ?? {},
      columnStates: columnStateMap,
      onFilterChange: handleFilterChange,
      onPinColumn: handlePinColumn,
      onAutosizeColumn: handleAutosizeColumn,
      onAutosizeAllColumns: handleAutosizeAllColumns,
      onResetColumns: handleResetColumns,
      onResetFilters: handleResetFilters,
      onSort: handleSortDirect,
    }),
    [
      showColumnMenu,
      showPinning,
      showAutosize,
      filterValues,
      columnStateMap,
      handleFilterChange,
      handlePinColumn,
      handleAutosizeColumn,
      handleAutosizeAllColumns,
      handleResetColumns,
      handleResetFilters,
      handleSortDirect,
    ]
  );

  // Extracted to avoid duplicating the scroll container + table structure between the
  // DndContext (columnMovingAllowed=true) and plain-div (columnMovingAllowed=false) branches.
  // The only difference between those branches is the outer DndContext wrapper and the DragOverlay.
  const tableScrollArea = (
    <div
      className={
        autoHeight
          ? 'overflow-x-auto overflow-y-visible w-full min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
          : 'flex-1 overflow-auto w-full min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
      }
    >
      {/* isColumnsReady is false until applyPresetColumnsAndSort has run once.
          Before that, column widths are defaults — rendering the header and
          column-aware skeleton would cause a visible width-jump flash.
          Instead we show a plain full-width skeleton with no column structure. */}
      {isColumnsReady ? (
        <table
          ref={tableRef}
          className="w-full text-sm table-fixed"
          style={{borderCollapse: 'separate', borderSpacing: 0}}
          aria-label={gridCode}
        >
          <colgroup ref={colgroupRef}>
            {/* checkbox pseudo-column: not part of columnStates, fixed width */}
            {hasCheckboxColumn && <col style={{width: SELECTION_COLUMN_WIDTH}} />}
            {/* left-pinned + unpinned columns */}
            {nonRightPinnedColumnStates.map((colState) => (
              <col key={colState.key} style={{width: colState.width ?? DEFAULT_COL_WIDTH}} />
            ))}
            {/* filler col: absorbs remaining space between unpinned and right-pinned columns.
                Positioned here so right-pinned columns naturally sit at the table's right edge
                regardless of horizontal overflow. */}
            <col />
            {/* right-pinned columns */}
            {rightPinnedColumnStates.map((colState) => (
              <col key={colState.key} style={{width: colState.width ?? DEFAULT_COL_WIDTH}} />
            ))}
            {/* action column col: fixed width, not resizable */}
            {actionColumnWidth > 0 && <col style={{width: actionColumnWidth}} />}
          </colgroup>
          <ColumnMenuContext.Provider value={columnMenuContextValue}>
            <HeaderRow
              columns={visibleColumns}
              columnStates={orderedVisibleColumnStates}
              onSort={handleSort}
              onResizeStart={resizeManager.onResizeStart}
              sortableIds={dragManager.sortableIds}
              pinnedLeftOffsets={pinnedLeftOffsets}
              pinnedRightOffsets={pinnedRightOffsets}
              columnConfig={columnConfig}
              columnGroupsVisible={columnGroupsVisible}
              lastPinnedLeftKey={lastPinnedLeftKey}
              firstPinnedRightKey={firstPinnedRightKey}
              actionColumnWidth={actionColumnWidth > 0 ? actionColumnWidth : undefined}
              headerHeightPx={LineHeightToPx[gridSettings.headerHeight]}
              colIndexMap={colIndexMap}
              rowSelectMode={rowSelectMode}
            />
          </ColumnMenuContext.Provider>
          {/* drag state (activeDragId, overColumnId, dropSide) is read from store by leaf components */}
          <GridBody
            columns={visibleColumns}
            columnStates={orderedVisibleColumnStates}
            rows={rows}
            previousRows={previousRows}
            skeletonRowCount={itemsPerPage}
            isLoading={isLoading}
            selectedRowIds={selectedRowIds}
            onRowClick={handleRowClick}
            pinnedLeftOffsets={pinnedLeftOffsets}
            pinnedRightOffsets={pinnedRightOffsets}
            stripedRows={gridSettings.stripedRows}
            lastPinnedLeftKey={lastPinnedLeftKey}
            firstPinnedRightKey={firstPinnedRightKey}
            onActionClick={actionColumnWidth > 0 ? handleActionClick : undefined}
            actionColumnWidth={actionColumnWidth > 0 ? actionColumnWidth : undefined}
            isMobile={isMobile}
            disableAllActions={store.getState().disableAllActions}
            rowSelectMode={rowSelectMode}
            onToggleSelection={handleToggleSelection}
          />
          {/* drag state (activeDragId, overColumnId, dropSide) is read from store by GridCell/SkeletonRow */}
        </table>
      ) : (
        <table className="w-full text-sm" style={{borderCollapse: 'separate', borderSpacing: 0}}>
          <tbody>
            {Array.from({length: itemsPerPage}, (_, i) => (
              <tr key={i} style={{height: LineHeightToPx[gridSettings.rowHeight]}}>
                <td className="p-2">
                  <div className="animate-pulse bg-gray-200 h-4 rounded" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div
      className={
        autoHeight
          ? 'flex flex-col overflow-hidden min-w-0 relative'
          : 'flex flex-col h-full overflow-hidden min-w-0 relative'
      }
    >
      <GridHeader
        definition={definition}
        dataGridSettings={gridSettings}
        // TODO: GridHeader.dataGridProps only reads gridCode — narrow the prop type to {gridCode: string}
        dataGridProps={{gridCode} as EagGridProps}
        columnStates={columnStates}
        refreshData={refreshData}
        dataQuery={dataQuery}
        activePanel={activePanel}
        onTogglePanel={handleTogglePanel}
        isFilterActive={isFilterActive}
        onResetFilters={handleResetFilters}
        data-testid={testId}
      />
      <AnimatePresence>
        {selectedRowIds.size > 0 && (
          <motion.div
            initial={BULK_BAR_ANIMATION.initial}
            animate={BULK_BAR_ANIMATION.animate}
            exit={BULK_BAR_ANIMATION.exit}
            transition={BULK_BAR_ANIMATION.transition}
          >
            <BulkActionsBar
              selectedCount={selectedRowIds.size}
              bulkActions={bulkActions}
              onDeselectAll={handleDeselectAll}
              onBulkAction={handleBulkAction}
              disableAllActions={store.getState().disableAllActions}
              isLoading={isBulkActionLoading}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={
          autoHeight
            ? 'flex flex-row overflow-hidden relative min-w-0'
            : 'flex flex-row flex-1 overflow-hidden relative min-w-0'
        }
      >
        <div
          className={
            autoHeight
              ? 'flex flex-col overflow-hidden relative min-w-0'
              : 'flex flex-col flex-1 overflow-hidden relative min-w-0'
          }
          onClick={handleTableAreaClick}
        >
          {/* DndContext must wrap a <div>, never a <table> directly.
              dnd-kit injects accessibility <div>s as DndContext children;
              placing them inside <table> is invalid HTML and triggers hydration errors.
              When columnMovingAllowed is false, render without DndContext wrapper. */}
          {columnMovingAllowed ? (
            <DndContext
              sensors={dnd.sensors}
              onDragStart={dnd.handleDragStart}
              onDragOver={dnd.handleDragOver}
              onDragEnd={dnd.handleDragEnd}
              onDragCancel={dnd.handleDragCancel}
            >
              {tableScrollArea}
              <DragOverlay>
                {dragOverlayColumn && dragOverlayColumnState ? (
                  <DragOverlayColumn
                    column={dragOverlayColumn}
                    columnState={dragOverlayColumnState}
                    rows={rows}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            tableScrollArea
          )}
          {isEmptyState && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto">{emptyStateNode}</div>
            </div>
          )}
        </div>
        <Sidebar
          columns={flatColumns}
          columnConfig={columnConfig}
          filterState={filterValues as Record<string, unknown>}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          settings={gridSettings}
          onSettingsChange={store.setGridSettings}
          filterComponent={
            // When externalFilterId is set, the filterComponent renders via portal (ExternalFiltersPortal below).
            // Pass undefined here to prevent duplicate rendering in the sidebar.
            gridContext.externalFilterId ? undefined : gridContext.filterComponent
          }
          activePanel={activePanel}
          onClose={handleClosePanel}
        />
      </div>
      {paginationEnabled && totalCount > 0 && (
        <GridPagination
          currentPage={page + 1}
          totalPages={Math.ceil(totalCount / itemsPerPage)}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}
      {/* ExternalFiltersPortal renders filterComponent into an external DOM element via React portal.
          Renders outside the table DOM tree but inside the store provider scope.
          Mutual exclusion: only renders when externalFilterId is set — sidebar uses native FilterPanel in that case. */}
      {gridContext.externalFilterId && gridContext.filterComponent && (
        <ExternalFiltersPortal
          externalFilterId={gridContext.externalFilterId}
          filterComponent={gridContext.filterComponent}
        />
      )}
    </div>
  );
}

/**
 * Thin wrapper that owns the imperative handle (ref) for external refresh.
 *
 * Splitting ref handling into a separate wrapper is required because
 * EagGridTableProps contains `ref: RefObject<...>`, which causes React
 * Compiler's taint analysis to flag every `props.xxx` read as a ref access.
 * By isolating `useImperativeHandle` here and forwarding ref-free props to
 * EagGridTableInner via `refreshRef`, the inner component is completely
 * taint-free.
 */
export function EagGridTable(props: EagGridTableProps) {
  // refreshRef is written by EagGridTableInner (once serverDataRef and offsetRef are
  // ready) and read here by useImperativeHandle to expose refreshData to the consumer.
  const refreshRef = useRef<(() => void) | null>(null);

  useImperativeHandle(props.ref, () => ({
    refreshData: () => {
      refreshRef.current?.();
    },
  }));

  /* eslint-disable react-hooks/refs -- EagGridTableProps contains `ref` (React 19 ref-as-prop);
     all props.xxx reads in this return block are tainted false positives.
     This is the intentional boundary where ref-taint is isolated and not propagated further. */
  return (
    <EagGridTableInner
      loadingCellRenderer={props.loadingCellRenderer}
      onPageChangeCallback={props.onPageChangeCallback}
      definition={props.definition}
      columnConfig={props.columnConfig}
      setVirtualPreset={props.setVirtualPreset}
      predefinedColumns={props.predefinedColumns}
      actionCallback={props.actionCallback}
      gridCode={props.gridCode}
      presetInstanceId={props.presetInstanceId}
      emptyState={props.emptyState}
      onRowSelectionChange={props.onRowSelectionChange}
      externalFilterId={props.externalFilterId}
      autoHeight={props.autoHeight}
      queryModifier={props.queryModifier}
      onFilterChanged={props.onFilterChanged}
      data-testid={props['data-testid']}
      refreshRef={refreshRef}
    />
  );
  /* eslint-enable react-hooks/refs */
}
